'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@/components/atoms/button';
import Link from '@/components/atoms/link';
import { CheckCircleIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import { CurrencyHelpers } from '@/utils/currency-helpers';
import toast from '@/components/atoms/toaster/helpers/toast';
import { ProductSuggestion } from '../../../search/types';
import { QuickOrderContentProps } from '../../types';
import ProductItem from './product-item';

const QuickOrderContent = ({
  addItem,
  addItemDisabled,
  onSkuLookup,
  initialItems,
  closeMenu,
  onDirtyChange,
}: QuickOrderContentProps) => {
  const translate = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);

  const [skuInput, setSkuInput] = useState('');
  const [skuError, setSkuError] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  // EWM-247 — SKUs the quickOrder/addItems endpoint couldn't add (unavailable
  // for this dealer / removed from their selection mid-session). Kept in the
  // drawer so the user can review or remove them instead of silent-dropping.
  const [failedSkus, setFailedSkus] = useState<string[]>([]);
  const [products, setProducts] = useState<ProductSuggestion[]>(initialItems ?? []);
  const [quantity, setQuantity] = useState<Record<string, number>>(
    Object.fromEntries((initialItems ?? []).map((p) => [p.sku, 1])),
  );

  // EWM-931 — report unsubmitted work upward so the Drawer can guard its close
  // paths. ANY populated row counts, including a partially typed SKU that hasn't
  // resolved to a product yet: a customer mid-keystroke has still done work
  // worth protecting. Rows already added to the cart are safe, so the
  // post-add success state is not dirty.
  const isDirty = !addedToCart && (products.length > 0 || skuInput.trim().length > 0);
  // Reported from an effect, never from the render body. `onDirtyChange` is the
  // PARENT's setState (`QuickOrderDesktop`'s `setIsDirty`), and calling that
  // while this component renders is the "Cannot update a component while
  // rendering a different component" violation — React 19.2 logs it on every
  // keystroke that flips dirtiness. The repo's adjust-state-during-render
  // pattern (EWM-760, the Drawer's `prevIsOpen`) is legal only for a
  // component's OWN state; this is the parent's.
  //
  // The effect also fixes the mount case the previous mirror silently skipped:
  // `useState(isDirty)` seeded the mirror to the initial value, so a content
  // mounted WITH rows never reported anything and the drawer's guard stayed
  // disarmed while unsubmitted work sat on screen.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const insertOrMerge = (product: ProductSuggestion) => {
    setFailedSkus([]);
    setProducts((prev) => {
      if (prev.find((p) => p.sku === product.sku)) return prev;
      return [...prev, product];
    });
    setQuantity((prev) => ({
      ...prev,
      [product.sku]: (prev[product.sku] ?? 0) + 1,
    }));
  };

  const removeProduct = (product: ProductSuggestion) => {
    setFailedSkus((prev) => prev.filter((sku) => sku !== product.sku));
    setProducts((prev) => prev.filter((p) => p.sku !== product.sku));
    setQuantity((prev) => {
      const next = { ...prev };
      delete next[product.sku];
      return next;
    });
  };

  const handleQuantityChange = (product: ProductSuggestion, value: number) => {
    if (value < 1) {
      removeProduct(product);
      return;
    }
    setQuantity((prev) => ({ ...prev, [product.sku]: value }));
  };

  const handleLookup = useCallback(async () => {
    const sku = skuInput.trim();
    if (!sku || !onSkuLookup) return;

    setSkuError('');
    setLookingUp(true);
    try {
      const result = await onSkuLookup(sku);
      if (result) {
        insertOrMerge(result);
        setSkuInput('');
        inputRef.current?.focus();
      } else {
        setSkuError(translate('quick-order.sku-not-found'));
      }
    } finally {
      setLookingUp(false);
    }
  }, [skuInput, onSkuLookup, translate]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLookup();
    }
  };

  const lineItems = useMemo(
    () => products.map(({ sku }) => ({ sku, count: quantity[sku] ?? 1 })),
    [products, quantity],
  );

  const subtotal = useMemo(() => {
    return products.reduce<ReturnType<typeof CurrencyHelpers.multiplyCurrency> | undefined>((acc, product) => {
      if (!product.price) return acc;
      const lineTotal = CurrencyHelpers.multiplyCurrency(product.price, quantity[product.sku] ?? 1);
      return acc ? CurrencyHelpers.addCurrency(acc, lineTotal) : lineTotal;
    }, undefined);
  }, [products, quantity]);

  const handleAddToCart = useCallback(async () => {
    setAddingToCart(true);
    setFailedSkus([]);
    // EWM-247 — quickOrder/addItems (QUICK-01) adds the purchasable rows and
    // returns failed[] for any it couldn't. Surface those inline instead of a
    // blanket success: keep the failed rows in the drawer, drop the added ones.
    try {
      const result = (await addItem?.(lineItems)) as
        { success?: boolean; failed?: Array<{ sku: string; reason: string }>; message?: string } | undefined;

      // Hard failure — the whole request was rejected (e.g. cart/price error),
      // so nothing was added. Show an error and stay on the form to retry
      // rather than a false success. Mirrors the sibling quick-order-upload
      // drawer's guard (EWM-605).
      if (result && result.success === false) {
        toast.error(result.message || translate('common.something-went-wrong'));
        return;
      }

      const failed = result?.failed ?? [];
      if (failed.length > 0) {
        const failedSet = new Set(failed.map((f) => f.sku));
        setFailedSkus(Array.from(failedSet));
        // Keep only the failed rows so the user can review / remove them; the
        // rest were added to the cart. Don't show the all-done success screen.
        setProducts((prev) => prev.filter((p) => failedSet.has(p.sku)));
        setQuantity((prev) => Object.fromEntries(Object.entries(prev).filter(([sku]) => failedSet.has(sku))));
        return;
      }

      setAddedToCart(true);
    } catch {
      toast.error(translate('common.something-went-wrong'));
    } finally {
      setAddingToCart(false);
    }
  }, [addItem, lineItems, translate]);

  const handleReset = useCallback(() => {
    setProducts([]);
    setQuantity({});
    setSkuInput('');
    setSkuError('');
    setAddedToCart(false);
    setFailedSkus([]);
    inputRef.current?.focus();
  }, []);

  const totalItemCount = lineItems.reduce((sum, li) => sum + li.count, 0);

  if (addedToCart) {
    return (
      <div className="flex flex-col items-center gap-6 px-6 py-20 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
          <CheckCircleIcon className="h-12 w-12 text-green-600" aria-hidden="true" />
        </div>
        <div>
          <h3 className="text-20 text-primary font-bold">{translate('quick-order.added-to-cart')}</h3>
          <p className="text-14 mt-2 text-gray-600">
            {translate('quick-order.added-to-cart-desc', { count: totalItemCount })}
          </p>
        </div>
        <div className="flex w-full max-w-xs flex-col gap-3">
          <Button variant="primary" size="m" className="w-full !rounded-lg !font-semibold" onClick={handleReset}>
            {translate('quick-order.add-more-items')}
          </Button>
          <Link
            href="/cart"
            onClick={closeMenu}
            className="border-primary text-14 text-primary hover:bg-primary/5 block w-full rounded-lg border py-3 text-center font-semibold"
          >
            {translate('quick-order.view-cart')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      <div className="p-6">
        {/* SKU input */}
        <div className="mb-6">
          <label className="text-14 mb-2 block font-semibold text-gray-700">{translate('quick-order.enter-sku')}</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon
                className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                ref={inputRef}
                type="text"
                value={skuInput}
                onChange={(e) => {
                  // EWM-575 — SKUs are uppercase; force alpha chars uppercase as the user types.
                  setSkuInput(e.target.value.toUpperCase());
                  if (skuError) setSkuError('');
                }}
                onKeyDown={handleKeyDown}
                placeholder={translate('quick-order.sku-placeholder')}
                disabled={addItemDisabled || !onSkuLookup}
                className="text-14 focus:border-primary focus:ring-primary/20 w-full rounded-lg border border-gray-200 py-3 pr-4 pl-10 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>
            <Button
              variant="primary"
              size="m"
              Icon={PlusIcon}
              iconPosition="left"
              loading={lookingUp}
              disabled={!skuInput.trim() || addItemDisabled || !onSkuLookup}
              onClick={handleLookup}
              className="shrink-0 !gap-2 !rounded-lg !px-4 !font-semibold"
            >
              {translate('quick-order.add-item')}
            </Button>
          </div>
          {skuError && <p className="text-13 mt-2 text-red-500">{skuError}</p>}
          <p className="text-12 mt-2 text-gray-500">{translate('quick-order.sku-helper')}</p>
        </div>

        {/* EWM-247 — inline surface for SKUs the endpoint couldn't add. */}
        {failedSkus.length > 0 && (
          <div role="alert" className="mb-6 rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-13 font-medium text-red-700">
              {translate('quick-order.items-not-added', { count: failedSkus.length })}
            </p>
          </div>
        )}

        {/* Product list / empty state */}
        {products.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center text-gray-400">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <MagnifyingGlassIcon className="h-8 w-8" aria-hidden="true" />
            </div>
            <p className="text-14">{translate('quick-order.empty-state')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-14 font-semibold text-gray-700">
                {translate('quick-order.products-count', { count: products.length })}
              </span>
              <button
                type="button"
                className="text-13 font-semibold text-red-500 hover:text-red-600"
                onClick={() => {
                  setProducts([]);
                  setQuantity({});
                }}
              >
                {translate('quick-order.clear-all')}
              </button>
            </div>
            {products.map((product) => (
              <ProductItem
                key={product.sku}
                product={product}
                quantity={quantity[product.sku] ?? 1}
                handleQuantityChange={handleQuantityChange}
                onRemove={removeProduct}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer — pinned to drawer bottom via mt-auto, stays visible while scrolling via sticky */}
      {products.length > 0 && (
        <div className="sticky bottom-0 mt-auto border-t border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <span className="text-14 text-gray-600">{translate('quick-order.subtotal-label')}</span>
              <span className="text-12 ml-1 text-gray-500">
                ({translate('quick-order.items-count', { count: totalItemCount })})
              </span>
            </div>
            {subtotal && (
              <span className="text-18 text-primary font-bold">{CurrencyHelpers.formatForCurrency(subtotal)}</span>
            )}
          </div>
          <Button
            loading={addingToCart}
            onClick={handleAddToCart}
            disabled={addItemDisabled}
            size="m"
            variant="primary"
            className="!text-16 w-full !rounded-lg !py-3.5 !font-semibold"
          >
            {translate('quick-order.add-all-to-cart')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default QuickOrderContent;
